import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { OfficeDb, CLOCK_KEY, DEVICE_KEY, getMeta, setMeta } from './db.js'
import { UNSYNCED, type ClockState } from './clock.js'
import { httpTransport } from './transport.js'
import { useSync, type SyncStatus } from './useSync.js'
import { uuidv7 } from '@office/domain'
import { SignIn } from '../auth/SignIn.js'
import {
  restore as restoreSession,
  signIn as sessionSignIn,
  signOut as sessionSignOut,
  clearSession,
  shouldForgetSession,
  type AuthAccount,
} from '../auth/session.js'

/**
 * App-wide singletons: the local mirror, the trusted clock correction, this
 * device's identity, and who is signed in.
 *
 * Deliberately tiny. Screens read data through repo/useQuery and write through
 * mutations; this only supplies the handles they need.
 */
export interface AppState {
  db: OfficeDb | null
  clockState: ClockState
  deviceId: string | null
  setClockState: (s: ClockState) => void
  /** Live sync status, so any screen can show it honestly. */
  sync: SyncStatus | null
  syncNow: () => void
  /**
   * The signed-in account, or null while nobody is.
   *
   * `account.personId` is the identity every write is attributed to — EXCEPT on
   * the iPad, whose account has no person and must ask who (see auth/actor.tsx).
   */
  account: AuthAccount | null
  /** Only bayo@sotech.com may change configuration (docs/06). The server enforces it too. */
  isAdmin: boolean
  /** Rejects with a `SignInError` whose message is written to be shown. */
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /**
   * Drop the session because the SERVER rejected it, not because anybody asked.
   * Takes the refused token so a 401 that lost a race with a fresh sign-in is
   * ignored instead of signing the new session straight back out.
   */
  forgetSession: (rejectedToken: string | null) => void
}

const Ctx = createContext<AppState>({
  db: null, clockState: UNSYNCED, deviceId: null, setClockState: () => {},
  sync: null, syncNow: () => {},
  account: null, isAdmin: false,
  signIn: async () => {}, signOut: async () => {}, forgetSession: () => {},
})

/** Boot state: the store is opening and the stored session has not been read yet. */
function Booting() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100dvh', display: 'grid', placeItems: 'center',
        background: 'var(--bg)', color: 'var(--text-subtle)',
      }}
    >
      Opening the office…
    </div>
  )
}

export function AppProvider({ children, dbName = 'office-os' }: { children: ReactNode; dbName?: string }) {
  const [db, setDb] = useState<OfficeDb | null>(null)
  const [clockState, setClock] = useState<ClockState>(UNSYNCED)
  const [deviceId, setDeviceId] = useState<string | null>(null)
  const [account, setAccount] = useState<AuthAccount | null>(null)
  /** False until the stored session has been read, so the gate never flashes. */
  const [authReady, setAuthReady] = useState(false)

  // Read through refs, not state, by things that must not be rebuilt when they
  // change: the transport reads the token on every request, and the sign-in
  // callbacks stay stable so the provider's value does not churn.
  const tokenRef = useRef<string | null>(null)
  const dbRef = useRef<OfficeDb | null>(null)
  const deviceIdRef = useRef<string | null>(null)

  useEffect(() => {
    let alive = true
    const instance = new OfficeDb(dbName)
    void (async () => {
      try {
        await instance.open()
        if (!alive) return
        const stored = await getMeta<ClockState>(instance, CLOCK_KEY, UNSYNCED)
        // The device id is minted once and kept: the outbox and the server must
        // agree on identity across restarts.
        let id = await getMeta<string | null>(instance, DEVICE_KEY, null)
        if (!id) {
          id = uuidv7(Date.now())
          await setMeta(instance, DEVICE_KEY, id)
        }
        // The session comes back out of IndexedDB, never off the network. A
        // signed-in wall tablet has to survive a power cut with the wifi down,
        // and tokens are long-lived by design, so there is nothing to refresh.
        const session = await restoreSession(instance)
        if (!alive) return
        tokenRef.current = session?.token ?? null
        dbRef.current = instance
        deviceIdRef.current = id
        setAccount(session?.account ?? null)
        setClock(stored); setDeviceId(id); setDb(instance); setAuthReady(true)
      } catch {
        // A blocked or unavailable IndexedDB must not blank the wall. The board
        // still renders from an empty context and says so.
        if (alive) { dbRef.current = null; setDb(null); setAuthReady(true) }
      }
    })()
    return () => { alive = false; instance.close() }
  }, [dbName])

  const setClockState = useMemo(() => (s: ClockState) => {
    setClock(s)
    if (db) void setMeta(db, CLOCK_KEY, s)
  }, [db])

  /**
   * The server refused a token, so it is worthless: drop it and let the gate ask
   * for a password. Retrying a revoked token forever is the head-of-line
   * blocking failure wearing an authentication hat.
   *
   * ONLY when it is still the token we are using. A 401 can outlive the request
   * that earned it: the sync loop runs while nobody is signed in, so a pull that
   * left unauthenticated can land a 401 *after* somebody has typed their
   * password — and signing that fresh session straight back out would leave the
   * form on screen with no explanation. The transport names the token it was
   * refused for exactly so this comparison is possible.
   */
  const forgetSession = useCallback((rejectedToken: string | null) => {
    /*
     * A 401 FOR A REQUEST THAT CARRIED NO TOKEN PROVES NOTHING.
     *
     * This was deleting a perfectly good session on every single boot, and the
     * wall tablet — the one device nobody is standing at to type a password —
     * was the worst affected.
     *
     * The sequence, traced: the sync loop fires its first pull before the
     * stored session has come back out of IndexedDB, so it goes out with no
     * Authorization header at all. The server answers 401, correctly. That 401
     * arrives here as `rejectedToken = null`, and `tokenRef.current` is ALSO
     * still null because the restore has not finished — so the equality below
     * held, and a valid, unrevoked token was wiped from the device.
     *
     * `null === null` is the one comparison this guard must never treat as a
     * match: the whole point of naming the refused token is to say WHICH
     * session the server refused, and "none" is not a session.
     */
    if (!shouldForgetSession(rejectedToken, tokenRef.current)) return
    tokenRef.current = null
    setAccount(null)
    void clearSession(dbRef.current)
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await sessionSignIn({
      db: dbRef.current, email, password, deviceId: deviceIdRef.current,
    })
    tokenRef.current = session.token
    setAccount(session.account)
  }, [])

  const signOut = useCallback(async () => {
    const token = tokenRef.current
    // Locally signed out before the network is even asked: a failed request is
    // not a reason to leave a session on a device somebody is walking away from.
    tokenRef.current = null
    setAccount(null)
    await sessionSignOut({ db: dbRef.current, token })
  }, [])

  // One transport for the app's lifetime. Recreating it on every render would
  // abort in-flight requests and could drop a push mid-flight — which is why the
  // token is read through a callback rather than captured here.
  const transport = useMemo(
    () => httpTransport({ token: () => tokenRef.current, onUnauthorized: forgetSession }),
    [forgetSession],
  )

  /**
   * The sync loop.
   *
   * Deliberately NOT a 30-second wall-clock timer: it syncs on mount, when the
   * network returns, when the tab becomes visible, and otherwise on a 5-15
   * minute idle floor. Polling harder is wasted cellular data on the phones and
   * pointless load on the VPS (docs/02 §8).
   */
  /*
   * NOT UNTIL THE SESSION HAS BEEN READ.
   *
   * `authReady` is false until the boot effect has finished asking IndexedDB
   * whether this device is signed in. Syncing before that answer arrives sends
   * a pull with no Authorization header, which the server refuses — a 401 that
   * says nothing except "you did not ask properly", and which used to cost the
   * device its stored session (see `forgetSession`). Waiting costs a few
   * milliseconds at boot and removes the whole class of problem.
   */
  const sync = useSync({ db, transport, deviceId, setClockState, enabled: authReady })

  const value = useMemo<AppState>(
    () => ({
      db, clockState, deviceId, setClockState, sync, syncNow: () => void sync.syncNow(),
      account, isAdmin: account?.isAdmin === true, signIn, signOut, forgetSession,
    }),
    [db, clockState, deviceId, setClockState, sync, account, signIn, signOut, forgetSession],
  )

  /**
   * The gate.
   *
   * Nothing renders until we know who is asking. Before sign-in existed the
   * phone used the DEVICE id as the actor, and the server rejected every write
   * with a foreign key violation — the outbox filled up and sync silently
   * stopped. An unauthenticated app has no honest identity to write with, so it
   * asks first.
   */
  return (
    <Ctx.Provider value={value}>
      {!authReady ? <Booting /> : account === null ? <SignIn onSignIn={signIn} /> : children}
    </Ctx.Provider>
  )
}

export const useApp = (): AppState => useContext(Ctx)
